import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, ElementRef, forwardRef, Inject, NgModule, QueryList, Renderer2, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { DomChangeModule } from '@app/directives/dom-change.directive';
import { StickyFillModule } from '@app/directives/stickyfill.directive';
import { LinkModule } from '@app/directives/link.directive';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';

@Component({
  selector: 'app-html-editor',
  templateUrl: './html-editor.component.html',
  styleUrls: ['./html-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HtmlEditorComponent),
      multi: true
    }
  ]
})
export class HtmlEditorComponent implements ControlValueAccessor {
  @ViewChild('editor', { static: true }) editor: ElementRef;
  @ViewChild('codeEditor', { static: true }) codeElement: ElementRef;
  @ViewChild('selector', { static: true }) selector: ElementRef;
  @ViewChildren(MatButton) actionBarButtons: QueryList<MatButton>;
  @ViewChild('lblLinkUrl', { static: true }) lblLinkUrl: ElementRef;
  @ViewChild('lblImageUrl', { static: true }) lblImageUrl: ElementRef;
  readonly formatBlock = 'formatBlock';
  private readonly defaultParagraphSeparator = `<defaultParagraphSeparator>`;
  private onChange;
  private onTouch;
  htmlMode = false;
  selectedHeading: string;

  constructor(@Inject(DOCUMENT) private document: Document, private renderer: Renderer2, private featureSettingsStoreService: FeatureSettingsStoreService) { }

  writeValue(obj: any): void {
    if (!obj) {
      obj = '';
    }
    if (this.htmlMode) {
      this.renderer.setProperty(this.codeElement.nativeElement, 'textContent', obj);
    } else {
      this.renderer.setProperty(this.editor.nativeElement, 'innerHTML', obj);
    }

  }

  getHeaderHeight() {
    if (this.featureSettingsStoreService.state.stickyHeader) {
      return this.document.querySelector('header').clientHeight;
    }
    return 0;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.renderer.addClass(this.editor.nativeElement, 'disabled');
    } else {
      this.renderer.removeClass(this.editor.nativeElement, 'disabled');
    }
  }

  changeEditorMode() {
    if (this.htmlMode) {
      this.htmlMode = false;
      this.renderer.setProperty(this.editor.nativeElement, 'innerHTML', this.codeElement.nativeElement.textContent);
      this.actionBarButtons.forEach(button => {
        button.disabled = false;
      });
      this.renderer.setProperty(this.selector.nativeElement, 'disabled', false);
    } else {
      this.htmlMode = true;
      this.renderer.setProperty(this.codeElement.nativeElement, 'textContent', this.editor.nativeElement.innerHTML);
      this.actionBarButtons.forEach(button => {
        if (button._elementRef.nativeElement.id.length > 0) {
          button.disabled = true;
        }
      });
      this.renderer.setProperty(this.selector.nativeElement, 'disabled', true);
    }
  }

  handleAction(command: string, value = null) {
    if (command === 'createLink') {
      const url = prompt(this.lblLinkUrl.nativeElement.innerHTML, 'https://');
      if (url.length > 8) {
        value = url;
      } else { return; }
    } else if (command === 'insertImage') {
      const url = prompt(this.lblImageUrl.nativeElement.innerHTML, 'https://');
      if (url.length > 8) {
        value = url;
      } else { return; }
    }
    this.exec(command, value);
  }

  exec(command, value = null): boolean {
    return this.document.execCommand(command, false, value);
  }

  queryCommandValue(command): string {
    return this.document.queryCommandValue(command);
  }

  queryCommandState(command): boolean {
    return this.document.queryCommandState(command);
  }

  private highlightSelectedActionBarButton(target: HTMLElement): void {
    if (target) {
      this.actionBarButtons.forEach(button => {
        const state = this.queryCommandState(button._elementRef.nativeElement.id);
        if (state) {
          this.renderer.addClass(button._elementRef.nativeElement, 'active');
        } else {
          this.renderer.removeClass(button._elementRef.nativeElement, 'active');
        }
      });

      const selectorElement = this.selector.nativeElement as HTMLSelectElement;
      if (selectorElement) {
        this.selectedHeading = '';
        for (let i = 0; i < selectorElement.options.length; i++) {
          if (selectorElement.options.item(i).value.toLowerCase() === target.nodeName.toLowerCase()) {
            this.selectedHeading = selectorElement.options.item(i).value;
            break;
          }
        }
      }
    }
  }

  private addOpenInNewWindowAttributeToLinks(target: HTMLElement): void {

    if (target.tagName === 'A') {
      target.setAttribute('target', '_blank');
    }
  }

  onClick(target: HTMLElement): void {
    this.highlightSelectedActionBarButton(target);
  }

  onDomChange(target: HTMLElement): void {
    if (target) {
      if (this.htmlMode) {
        this.onChange(this.codeElement.nativeElement.textContent);
        this.onTouch(this.codeElement.nativeElement.textContent);
      } else {
        if (target.firstChild && target.firstChild.nodeType === 3) {
          this.exec(this.formatBlock, this.defaultParagraphSeparator);
        }
        this.addOpenInNewWindowAttributeToLinks(target);
        this.onChange(this.editor.nativeElement.innerHTML);
        this.onTouch(this.editor.nativeElement.innerHTML);
        this.highlightSelectedActionBarButton(target);

      }
    }
  }
}
@NgModule({
  declarations: [HtmlEditorComponent],
  exports: [HtmlEditorComponent],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    DomChangeModule,
    LinkModule,
    StickyFillModule
  ]
})
export class HtmlEditorModule { }
